import { UnifiedPrioritySelector } from '@/components/global/selectors/UnifiedPrioritySelector';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type PriorityOption } from '@/types';
import { Loader2 } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';

interface InlinePriorityCellProps {
    value: string | null | undefined;
    onUpdate?: (value: string) => Promise<void>;
    className?: string;
    priorities?: PriorityOption[];
    readOnly?: boolean;
}

export function InlinePriorityCell({ value, onUpdate, className, priorities = [], readOnly = false }: InlinePriorityCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: string) => {
        if (newValue === value || !onUpdate) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update priority:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const options = priorities.map((priority) => ({ value: priority.value, label: priority.label }));
    const hasOptions = options.length > 0;
    const openSelector = (event?: MouseEvent | KeyboardEvent) => {
        event?.stopPropagation();
        if (!isLoading) {
            setIsOpen(true);
        }
    };

    // Read-only mode: just display the priority
    if (readOnly || !onUpdate || !hasOptions) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <UnifiedPrioritySelector value={value || 'medium'} priorityType="lead" mode="readonly" />
            </div>
        );
    }

    if (isMobile) {
        return (
            <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
                <ResponsiveModalTrigger asChild>
                    <div
                        role="button"
                        tabIndex={isLoading ? -1 : 0}
                        onClick={openSelector}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openSelector(event);
                            }
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        aria-disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <UnifiedPrioritySelector value={value || 'medium'} priorityType="lead" mode="readonly" />
                        )}
                    </div>
                </ResponsiveModalTrigger>
                <ResponsiveModalContent dialogClassName="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('leads.index.columns.priority', 'Priority')}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <UnifiedPrioritySelector value={option.value} priorityType="lead" mode="readonly" />
                            </button>
                        ))}
                    </ResponsiveModalBody>
                </ResponsiveModalContent>
            </ResponsiveModal>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <div
                    role="button"
                    tabIndex={isLoading ? -1 : 0}
                    onClick={openSelector}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openSelector(event);
                        }
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    aria-disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <UnifiedPrioritySelector value={value || 'medium'} priorityType="lead" mode="readonly" />
                    )}
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <UnifiedPrioritySelector value={option.value} priorityType="lead" mode="readonly" />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
